import { httpGetContent, httpPostContent } from '@pressreader/content-api';
import { baseUrl, httpGet, httpPost } from '@pressreader/services';
import type { Direction } from '@pressreader/types';

import type {
    ChannelFeedDto,
    FollowResultDto,
    ProfilesPageDto,
    SocialInfoDto,
    SocialProfileDto,
    UserProfileFullDto,
    UsersChannelsSearchResultDto,
} from '../types';

const service = 'social';
const usersV2Service = 'v2/users';

function getProfilePath(profileId: string): string {
    return `profiles/${profileId}`;
}

function getBriefSocialProfile(profileId = 'current') {
    return httpGetContent<SocialProfileDto>(service, `${getProfilePath(profileId)}/brief`, {});
}

async function getChannelFeed(
    profileId: string,
    data: {
        token: string;
        direction: Direction;
        pageSize?: number;
        bookmarkId?: string;
    },
) {
    if (!profileId) {
        return Promise.reject('ProfileId not specified');
    }
    return httpGet<ChannelFeedDto>(service, `${getProfilePath(profileId)}/feed`, data);
}

function getCurrentUserProfile() {
    return httpGetContent<SocialInfoDto>(service, 'profiles/current', {});
}

function getFullUserProfile(profileId: string) {
    return httpGetContent<UserProfileFullDto>(usersV2Service, `${profileId}/full`, {});
}

function getUserProfileName(enAccountNumber: string) {
    return httpGetContent<string>(service, 'GetUserProfileName', { enAccountNumber });
}

function getProfilePhotoUrl(profileId: string) {
    return httpGetContent<string>(service, `${getProfilePath(profileId)}/photo/url`, {});
}

function getProfileAvatarImageId(profileId: string) {
    return httpGetContent<string>(usersV2Service, `${profileId}/avatar`, {});
}

function followProfile(profileId: string) {
    return httpPostContent<FollowResultDto>(service, `${getProfilePath(profileId)}/follow`);
}

function unfollowProfile(profileId: string) {
    return httpPostContent<FollowResultDto>(service, `${getProfilePath(profileId)}/unfollow`);
}

interface ProfilesPageParams {
    filter?: string;
    skip?: number;
    take?: number;
    orderBy?: string;
}

function getProfileFollowers(profileId: string, params: ProfilesPageParams = {}) {
    return httpGetContent<ProfilesPageDto>(service, `${getProfilePath(profileId)}/followers`, params);
}

function getProfileFollows(profileId: string, params: ProfilesPageParams = {}) {
    return httpGetContent<ProfilesPageDto>(service, `${getProfilePath(profileId)}/follows`, params);
}

function uploadProfilePhoto(photoFile: Blob) {
    const formData = new FormData();
    formData.append('PhotoFile', photoFile);

    return httpPost<unknown>({
        url: `${baseUrl()}${service}/UploadPhoto`,
        data: formData,
        requestConfig: { withCredentials: true },
    });
}

function getStaticMapUrl(width: number, height: number, zoom: number, lat: number, lng: number): string {
    return `${baseUrl()}map?width=${width}&height=${height}&zoom=${zoom}&centerLat=${lat}&centerLng=${lng}`;
}

interface UsersChannelsSearchParams {
    filter?: string;
    offset?: number;
    limit?: number;
    orderBy?: string;
}

function searchUsersChannels({ filter, offset, limit, orderBy }: UsersChannelsSearchParams): Promise<UsersChannelsSearchResultDto> {
    // call to channels/search with a filter less than 3 characters long fails with 500 status
    if (filter && filter.length < 3) {
        return Promise.resolve({
            items: [],
            meta: { offset, limit, totalCount: 0 },
        });
    }

    return httpGet<UsersChannelsSearchResultDto>('channels', 'search', { offset, limit, filter, orderBy });
}

export {
    followProfile,
    getBriefSocialProfile,
    getChannelFeed,
    getCurrentUserProfile,
    getFullUserProfile,
    getProfileAvatarImageId,
    getProfileFollowers,
    getProfileFollows,
    getProfilePhotoUrl,
    getStaticMapUrl,
    getUserProfileName,
    searchUsersChannels,
    unfollowProfile,
    uploadProfilePhoto,
};
